import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  queryKeys,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { useTranslations } from 'use-intl';
import { DocumentsContextProvider } from '@/app/components/shared/EntityDocuments/DocumentsContext';
import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { PlusSignIcon, AuctionIcon } from '@hugeicons/core-free-icons';
import { deleteBidDocuments } from '@/app/services/bidsService';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderButton,
} from '@/app/ui/components/header';
import { MainScrollArea } from '@/app/ui/components/main';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { FilesList } from '@/app/components/shared/EntityDocuments/FilesList';
import { Document } from '@/app/types/models';
import { FileUploadDialog } from '@/app/components/shared/EntityDocuments/FileUploadDialog/FileUploadDialog';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/files/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tFiles = useTranslations('pages.files');
  const navigate = useNavigate();
  const { bidId, projectId, tenderPackageId } = Route.useParams();
  const [open, setOpen] = useState(false);
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const handleRowClick = (doc: Document) => {
    navigate({
      to: '/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/files/$documentId',
      params: { projectId, tenderPackageId, bidId, documentId: doc.id },
    });
  };

  return (
    <DocumentsContextProvider
      resourceId={bidId}
      documentsType={DocumentTypes.BID}
      detailsQueryKey={[queryKeys.BID_DETAILS, bidId]}
    >
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('nav.files')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderButton aria-label={tFiles('addFiles')} onClick={() => setOpen(true)}>
          <HugeiconsIcon icon={PlusSignIcon} />
        </HeaderButton>
      </Header>
      <MainScrollArea>
        <DocumentAlerts resourceId={bidId} summaryType={SummarizationNamespaces.BID} />
        <FilesList
          resourceId={bidId}
          resourceType={DocumentTypes.BID}
          onAddClickAction={() => setOpen(true)}
          deleteDocumentsAction={deleteBidDocuments}
          detailsQueryKey={[queryKeys.BID_DETAILS, bidId]}
          showVersions
          onRowClick={handleRowClick}
        />
      </MainScrollArea>
      <FileUploadDialog
        resourceId={bidId}
        resourceType={DocumentTypes.BID}
        open={open}
        onOpenChange={setOpen}
        detailsQueryKey={[queryKeys.BID_DETAILS, bidId]}
      />
    </DocumentsContextProvider>
  );
}
